/**
 * PropertyPanel — 右侧属性面板
 *
 * Shows editable properties for the currently selected element,
 * or template-level settings when nothing is selected.
 */

import { type DesignElement, type DesignTemplate } from './types';
import { Settings2, Type, Hash, AlignLeft, AlignCenter, AlignRight, Bold, Italic, Palette, Trash2 } from 'lucide-react';

interface PropertyPanelProps {
  template: DesignTemplate;
  selectedElement: DesignElement | null;
  selectedBandId: string | null;
  onUpdateElementProps: (props: Partial<Record<string, unknown>>) => void;
  onUpdatePosition: (x: number, y: number, w: number, h: number) => void;
  onDeleteElement: () => void;
  onUpdateTemplateMeta: (meta: Partial<Pick<DesignTemplate, 'name' | 'pageSize' | 'orientation' | 'margins'>>) => void;
  collapsed?: boolean;
}

export default function PropertyPanel({
  template,
  selectedElement,
  selectedBandId,
  onUpdateElementProps,
  onUpdatePosition,
  onDeleteElement,
  onUpdateTemplateMeta,
  collapsed = false,
}: PropertyPanelProps) {
  if (collapsed) return null;

  return (
    <div className="w-64 shrink-0 border-l border-border bg-muted/30 flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b border-border flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        <Settings2 className="h-3.5 w-3.5" />
        {selectedElement ? 'Element' : 'Page'}
      </div>

      <div className="flex-1 overflow-y-auto p-3 text-xs space-y-4">
        {selectedElement ? (
          <ElementProperties
            element={selectedElement}
            onUpdateProps={onUpdateElementProps}
            onUpdatePosition={onUpdatePosition}
            onDelete={onDeleteElement}
          />
        ) : (
          <TemplateProperties
            template={template}
            onUpdate={onUpdateTemplateMeta}
          />
        )}
      </div>
    </div>
  );
}

// ── Element Properties ───────────────────────────────────────

function ElementProperties({
  element,
  onUpdateProps,
  onUpdatePosition,
  onDelete,
}: {
  element: DesignElement;
  onUpdateProps: (props: Partial<Record<string, unknown>>) => void;
  onUpdatePosition: (x: number, y: number, w: number, h: number) => void;
  onDelete: () => void;
}) {
  return (
    <>
      {/* Element type badge */}
      <div className="flex items-center gap-2 pb-2 border-b border-border/60">
        <span className="text-sm">{getTypeEmoji(element.type)}</span>
        <span className="font-medium text-foreground">{element.type}</span>
        <span className="ml-auto text-[10px] text-muted-foreground font-mono">{element.id.slice(0, 8)}</span>
      </div>

      {/* Position / Size */}
      <Section title="Position & Size">
        <div className="grid grid-cols-2 gap-2">
          <NumberInput label="X" value={element.x} onChange={(v) => onUpdatePosition(v, element.y, element.width, element.height)} />
          <NumberInput label="Y" value={element.y} onChange={(v) => onUpdatePosition(element.x, v, element.width, element.height)} />
          <NumberInput label="W" value={element.width} onChange={(v) => onUpdatePosition(element.x, element.y, v, element.height)} min={16} />
          <NumberInput label="H" value={element.height} onChange={(v) => onUpdatePosition(element.x, element.y, element.width, v)} min={8} />
        </div>
      </Section>

      {/* Type-specific properties */}
      {element.type === 'Label' && (
        <LabelProps element={element} onUpdate={onUpdateProps} />
      )}
      {element.type === 'Field' && (
        <FieldProps element={element} onUpdate={onUpdateProps} />
      )}
      {element.type === 'Image' && (
        <ImageProps element={element} onUpdate={onUpdateProps} />
      )}
      {element.type === 'Table' && (
        <TableProps element={element} onUpdate={onUpdateProps} />
      )}
      {element.type === 'Line' && (
        <LineProps element={element} onUpdate={onUpdateProps} />
      )}
      {element.type === 'Rectangle' && (
        <RectangleProps element={element} onUpdate={onUpdateProps} />
      )}
      {element.type === 'Barcode' && (
        <BarcodeProps element={element} onUpdate={onUpdateProps} />
      )}
      {element.type === 'Checkbox' && (
        <CheckboxProps element={element} onUpdate={onUpdateProps} />
      )}
      {element.type === 'SummaryField' && (
        <SummaryFieldProps element={element} onUpdate={onUpdateProps} />
      )}

      {/* Delete */}
      <div className="pt-2 border-t border-border/60">
        <button
          onClick={onDelete}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs text-red-500
                     border border-red-200 dark:border-red-800 rounded hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
        >
          <Trash2 className="h-3 w-3" />
          Delete Element
        </button>
      </div>
    </>
  );
}

// ── Label Properties ─────────────────────────────────────────

function LabelProps({ element, onUpdate }: { element: DesignElement; onUpdate: (p: Record<string, unknown>) => void }) {
  return (
    <>
      <Section title="Text">
        <textarea
          className="w-full rounded border border-border bg-background px-2 py-1 text-xs resize-none focus:ring-1 focus:ring-ring focus:outline-none"
          rows={2}
          value={(element.props.text as string) ?? ''}
          onChange={(e) => onUpdate({ text: e.target.value })}
          placeholder="Enter label text..."
        />
      </Section>
      <Section title="Font">
        <NumberInput
          label="Size"
          value={(element.props.fontSize as number) ?? 10}
          onChange={(v) => onUpdate({ fontSize: v })}
          min={6}
          max={72}
        />
        <div className="flex gap-1 mt-1.5">
          <ToggleBtn
            active={!!element.props.bold}
            onClick={() => onUpdate({ bold: !element.props.bold })}
            title="Bold"
          >
            <Bold className="h-3 w-3" />
          </ToggleBtn>
          <ToggleBtn
            active={!!element.props.italic}
            onClick={() => onUpdate({ italic: !element.props.italic })}
            title="Italic"
          >
            <Italic className="h-3 w-3" />
          </ToggleBtn>
          <div className="w-px bg-border mx-0.5" />
          <ToggleBtn
            active={element.props.textAlign === 'left'}
            onClick={() => onUpdate({ textAlign: 'left' })}
            title="Align Left"
          >
            <AlignLeft className="h-3 w-3" />
          </ToggleBtn>
          <ToggleBtn
            active={element.props.textAlign === 'center'}
            onClick={() => onUpdate({ textAlign: 'center' })}
            title="Align Center"
          >
            <AlignCenter className="h-3 w-3" />
          </ToggleBtn>
          <ToggleBtn
            active={element.props.textAlign === 'right'}
            onClick={() => onUpdate({ textAlign: 'right' })}
            title="Align Right"
          >
            <AlignRight className="h-3 w-3" />
          </ToggleBtn>
        </div>
      </Section>
      <Section title="Color">
        <ColorInput
          label="Text"
          value={(element.props.color as string) ?? '#000000'}
          onChange={(v) => onUpdate({ color: v })}
        />
        <ColorInput
          label="Background"
          value={(element.props.backgroundColor as string) ?? 'transparent'}
          onChange={(v) => onUpdate({ backgroundColor: v })}
        />
      </Section>
    </>
  );
}

// ── Field Properties ─────────────────────────────────────────

function FieldProps({ element, onUpdate }: { element: DesignElement; onUpdate: (p: Record<string, unknown>) => void }) {
  return (
    <>
      <Section title="Data Binding">
        <TextInput
          label="Field"
          value={(element.props.dataBinding as string) ?? ''}
          onChange={(v) => onUpdate({ dataBinding: v })}
          placeholder="e.g. customer_name"
          mono
        />
      </Section>
      <Section title="Format">
        <SelectInput
          label="Type"
          value={(element.props.format as string) ?? 'text'}
          options={[
            { value: 'text', label: 'Text' },
            { value: 'number', label: 'Number' },
            { value: 'currency', label: 'Currency' },
            { value: 'date', label: 'Date' },
            { value: 'datetime', label: 'Date/Time' },
            { value: 'percentage', label: 'Percentage' },
          ]}
          onChange={(v) => onUpdate({ format: v })}
        />
        <TextInput
          label="Pattern"
          value={(element.props.formatPattern as string) ?? ''}
          onChange={(v) => onUpdate({ formatPattern: v })}
          placeholder="e.g. #,##0.00"
          mono
        />
      </Section>
    </>
  );
}

// ── Image Properties ─────────────────────────────────────────

function ImageProps({ element, onUpdate }: { element: DesignElement; onUpdate: (p: Record<string, unknown>) => void }) {
  return (
    <Section title="Image">
      <TextInput
        label="URL / Field"
        value={(element.props.src as string) ?? ''}
        onChange={(v) => onUpdate({ src: v })}
        placeholder="https://... or {{logo}}"
      />
      <SelectInput
        label="Fit"
        value={(element.props.objectFit as string) ?? 'contain'}
        options={[
          { value: 'contain', label: 'Contain' },
          { value: 'cover', label: 'Cover' },
          { value: 'fill', label: 'Stretch' },
        ]}
        onChange={(v) => onUpdate({ objectFit: v })}
      />
    </Section>
  );
}

// ── Table Properties ─────────────────────────────────────────

function TableProps({ element, onUpdate }: { element: DesignElement; onUpdate: (p: Record<string, unknown>) => void }) {
  return (
    <Section title="Table">
      <TextInput
        label="Data Source"
        value={(element.props.dataSource as string) ?? ''}
        onChange={(v) => onUpdate({ dataSource: v })}
        placeholder="e.g. line_items"
        mono
      />
      <div className="mt-1 text-[10px] text-muted-foreground">
        Column configuration will be available in the full editor.
      </div>
    </Section>
  );
}

// ── Line Properties ──────────────────────────────────────────

function LineProps({ element, onUpdate }: { element: DesignElement; onUpdate: (p: Record<string, unknown>) => void }) {
  return (
    <Section title="Line">
      <NumberInput
        label="Thickness"
        value={(element.props.thickness as number) ?? 1}
        onChange={(v) => onUpdate({ thickness: v })}
        min={1} max={10}
      />
      <ColorInput
        label="Color"
        value={(element.props.color as string) ?? '#333333'}
        onChange={(v) => onUpdate({ color: v })}
      />
    </Section>
  );
}

// ── Rectangle Properties ─────────────────────────────────────

function RectangleProps({ element, onUpdate }: { element: DesignElement; onUpdate: (p: Record<string, unknown>) => void }) {
  return (
    <Section title="Rectangle">
      <ColorInput
        label="Fill"
        value={(element.props.fillColor as string) ?? 'transparent'}
        onChange={(v) => onUpdate({ fillColor: v })}
      />
      <ColorInput
        label="Border"
        value={(element.props.borderColor as string) ?? '#999999'}
        onChange={(v) => onUpdate({ borderColor: v })}
      />
      <NumberInput
        label="Border Width"
        value={(element.props.borderWidth as number) ?? 1}
        onChange={(v) => onUpdate({ borderWidth: v })}
        min={0} max={10}
      />
      <NumberInput
        label="Radius"
        value={(element.props.borderRadius as number) ?? 0}
        onChange={(v) => onUpdate({ borderRadius: v })}
        min={0} max={50}
      />
    </Section>
  );
}

// ── Barcode Properties ───────────────────────────────────────

function BarcodeProps({ element, onUpdate }: { element: DesignElement; onUpdate: (p: Record<string, unknown>) => void }) {
  return (
    <Section title="Barcode">
      <SelectInput
        label="Type"
        value={(element.props.barcodeType as string) ?? 'Code128'}
        options={[
          { value: 'Code128', label: 'Code 128' },
          { value: 'Code39', label: 'Code 39' },
          { value: 'QR', label: 'QR Code' },
          { value: 'EAN13', label: 'EAN-13' },
          { value: 'UPC', label: 'UPC-A' },
        ]}
        onChange={(v) => onUpdate({ barcodeType: v })}
      />
      <TextInput
        label="Data Field"
        value={(element.props.dataBinding as string) ?? ''}
        onChange={(v) => onUpdate({ dataBinding: v })}
        placeholder="e.g. tracking_number"
        mono
      />
    </Section>
  );
}

// ── Checkbox Properties ──────────────────────────────────────

function CheckboxProps({ element, onUpdate }: { element: DesignElement; onUpdate: (p: Record<string, unknown>) => void }) {
  return (
    <Section title="Checkbox">
      <TextInput
        label="Data Field"
        value={(element.props.dataBinding as string) ?? ''}
        onChange={(v) => onUpdate({ dataBinding: v })}
        placeholder="e.g. is_verified"
        mono
      />
      <TextInput
        label="Label"
        value={(element.props.label as string) ?? ''}
        onChange={(v) => onUpdate({ label: v })}
        placeholder="Display label"
      />
    </Section>
  );
}

// ── SummaryField Properties ──────────────────────────────────

function SummaryFieldProps({ element, onUpdate }: { element: DesignElement; onUpdate: (p: Record<string, unknown>) => void }) {
  return (
    <Section title="Summary">
      <SelectInput
        label="Function"
        value={(element.props.func as string) ?? 'sum'}
        options={[
          { value: 'sum', label: 'Sum (∑)' },
          { value: 'count', label: 'Count (#)' },
          { value: 'avg', label: 'Average (x̄)' },
          { value: 'min', label: 'Min' },
          { value: 'max', label: 'Max' },
        ]}
        onChange={(v) => onUpdate({ func: v })}
      />
      <TextInput
        label="Data Field"
        value={(element.props.dataBinding as string) ?? ''}
        onChange={(v) => onUpdate({ dataBinding: v })}
        placeholder="e.g. amount"
        mono
      />
      <SelectInput
        label="Running"
        value={(element.props.running as string) ?? 'report'}
        options={[
          { value: 'report', label: 'Report' },
          { value: 'group', label: 'Group' },
          { value: 'page', label: 'Page' },
        ]}
        onChange={(v) => onUpdate({ running: v })}
      />
    </Section>
  );
}

// ── Template Properties ──────────────────────────────────────

function TemplateProperties({
  template,
  onUpdate,
}: {
  template: DesignTemplate;
  onUpdate: (meta: Partial<Pick<DesignTemplate, 'name' | 'pageSize' | 'orientation' | 'margins'>>) => void;
}) {
  return (
    <>
      <Section title="Template">
        <TextInput
          label="Name"
          value={template.name}
          onChange={(v) => onUpdate({ name: v })}
          placeholder="Template name"
        />
      </Section>
      <Section title="Page">
        <SelectInput
          label="Size"
          value={template.pageSize}
          options={[
            { value: 'A4', label: 'A4 (210 × 297 mm)' },
            { value: 'A3', label: 'A3 (297 × 420 mm)' },
            { value: 'Letter', label: 'Letter (8.5 × 11 in)' },
            { value: 'Legal', label: 'Legal (8.5 × 14 in)' },
          ]}
          onChange={(v) => onUpdate({ pageSize: v as DesignTemplate['pageSize'] })}
        />
        <SelectInput
          label="Orientation"
          value={template.orientation}
          options={[
            { value: 'portrait', label: '📐 Portrait' },
            { value: 'landscape', label: '📐 Landscape' },
          ]}
          onChange={(v) => onUpdate({ orientation: v as 'portrait' | 'landscape' })}
        />
      </Section>
      <Section title="Margins (mm)">
        <div className="grid grid-cols-2 gap-2">
          <NumberInput label="Top" value={template.margins.top} onChange={(v) => onUpdate({ margins: { ...template.margins, top: v } })} min={0} />
          <NumberInput label="Bottom" value={template.margins.bottom} onChange={(v) => onUpdate({ margins: { ...template.margins, bottom: v } })} min={0} />
          <NumberInput label="Left" value={template.margins.left} onChange={(v) => onUpdate({ margins: { ...template.margins, left: v } })} min={0} />
          <NumberInput label="Right" value={template.margins.right} onChange={(v) => onUpdate({ margins: { ...template.margins, right: v } })} min={0} />
        </div>
      </Section>
    </>
  );
}

// ── Shared Input Components ──────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{title}</h4>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function NumberInput({
  label,
  value,
  onChange,
  min = 0,
  max = 9999,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-[10px] text-muted-foreground w-6 shrink-0">{label}</span>
      <input
        type="number"
        className="w-full rounded border border-border bg-background px-1.5 py-0.5 text-xs font-mono
                   focus:ring-1 focus:ring-ring focus:outline-none [appearance:textfield]
                   [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        value={Math.round(value)}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </label>
  );
}

function TextInput({
  label,
  value,
  onChange,
  placeholder,
  mono = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <input
        type="text"
        className={`w-full rounded border border-border bg-background px-2 py-1 text-xs
                    focus:ring-1 focus:ring-ring focus:outline-none mt-0.5 ${mono ? 'font-mono' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

function SelectInput({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <select
        className="w-full rounded border border-border bg-background px-2 py-1 text-xs
                   focus:ring-1 focus:ring-ring focus:outline-none mt-0.5"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </label>
  );
}

function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-1.5">
      <input
        type="color"
        className="w-5 h-5 rounded border border-border cursor-pointer p-0"
        value={value === 'transparent' ? '#ffffff' : value}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <input
        type="text"
        className="flex-1 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] font-mono
                   focus:ring-1 focus:ring-ring focus:outline-none"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function ToggleBtn({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`p-1 rounded transition-colors ${
        active
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:bg-accent'
      }`}
    >
      {children}
    </button>
  );
}

// ── Helpers ──────────────────────────────────────────────────

function getTypeEmoji(type: string): string {
  const map: Record<string, string> = {
    Label: '🏷️', Field: '📊', Image: '🖼️', Table: '📋',
    Line: '📏', Rectangle: '⬜', Barcode: '📊', Checkbox: '☑️',
    SummaryField: '🔢', PageNumber: '📄',
  };
  return map[type] ?? '📦';
}
